﻿import medicineKnowledgeBase from '../data/bdMedicineKnowledgeBase.json';
import type { Medicine } from '../types';
import { isKnownGenericName } from './medicineValidator';

interface MedicineKnowledgeEntry {
  brandName: string;
  genericName: string;
  strength: string;
  form: string;
  category: string;
  commonUse: string;
  riskNotes: string;
  confidenceSource: string;
}

interface ResolveResult {
  status: 'identified' | 'needs_review' | 'possible_lookalike';
  entry?: MedicineKnowledgeEntry;
  confidence: number;
  normalizedName: string;
  possibleMatches: MedicineKnowledgeEntry[];
  reason?: string;
}

const KNOWLEDGE_BASE = medicineKnowledgeBase as MedicineKnowledgeEntry[];

const OCR_VARIANTS: Record<string, string> = {
  ANGLICOK: 'ANGILOCK',
  ANGLICLOCK: 'ANGILOCK',
  ANGILOCK: 'ANGILOCK',
  ANGILOC: 'ANGILOCK',
  ANGILOK: 'ANGILOCK',
  ANGLOCK: 'ANGILOCK',
  ACTRAPIDHM: 'ACTRAPID HM',
  ACTRAPIDHMI: 'ACTRAPID HM',
  NAPARAPID: 'NAPA RAPID',
  DOXYCAP: 'DOXICAP',
  DOXCAP: 'DOXICAP',
  UROMAXD: 'UROMAX D',
  XIOMOX: 'XIOMOX',
  XIOX10X: 'XIOMOX',
  XIOXIOX: 'XIOMOX',
  EFODIO: 'EFODIO',
  EFO10: 'EFODIO',
  DEXILEND: 'DEXILEND',
  DEXILENT: 'DEXILEND',
  DOXIVA400: 'DOXIVA',
  WINDELPLUS: 'WINDEL PLUS',
  BUDICORTRESPULES: 'BUDICORT',
  GLYMPON: 'GLYMPON',
  GLYMPA: 'GLYMPA',
  GLYMPON255: 'GLYMPON',
  GLYMPA255: 'GLYMPA',
  NEUROB: 'NEURO B',
  NEURO: 'NEURO B',
  PREGAB: 'PREGABA',
  PREGABALIN: 'PREGABA',
  PREGABA50: 'PREGABA',
  EGGCALDX: 'EGGCAL DX',
  EGGCAL: 'EGGCAL DX',
  MONOCAST10: 'MONOCAST',
  AQUAFRESH: 'AQUAFRESH E/D',
  AQUAFRESHED: 'AQUAFRESH E/D',
  AQUAFRESHEYE: 'AQUAFRESH E/D',
  AQUAFRESHEYEDROP: 'AQUAFRESH E/D',
  FENADIN120: 'FENADIN',
  FENDON: 'FENADIN',
  FENDON120: 'FENADIN',
  AFRINNASALDROP: 'AFRIN NASAL DROP',
  AFRINNASAL: 'AFRIN NASAL DROP',
  AFRINND: 'AFRIN NASAL DROP',
  FUROTILPLUS: 'FUROTIL PLUS',
  NS: 'NORMAL SALINE',
  NORMALSALINE: 'NORMAL SALINE',
  AERON10: 'AERON',
  OXOFYLLINE400: 'OXOFYLLINE',
  FLAMEX400: 'FLAMEX',
  PERMETHRINCREAM: 'PERMETHRIN CREAM',
  PERMETHRIN5: 'PERMETHRIN CREAM',
  CEFUROXIMESYP: 'CEFUROXIME SYRUP',
  CEFUROXIMESYRUP: 'CEFUROXIME SYRUP',
  CETIRIZINESYP: 'CETIRIZINE SYRUP',
  CETIRIZINESYRUP: 'CETIRIZINE SYRUP',
  ACEXR: 'ACE XR',
  AEXR: 'ACE XR',
  AEX: 'ACE XR',
  NAPROXENESOMEPRAZOLE: 'NAPROXEN + ESOMEPRAZOLE',
  NAPROXENESOMOPLAZOLE: 'NAPROXEN + ESOMEPRAZOLE',
  NAPROXENESOMEPRAZOLE20: 'NAPROXEN + ESOMEPRAZOLE',
  VITAMINB1B6B12: 'VITAMIN B1 B6 B12',
  VITB1B6B12: 'VITAMIN B1 B6 B12',
  BACLOFEN10: 'BACLOFEN',
  CLONAZEPAM05: 'CLONAZEPAM',
  PREGABALIN25: 'PREGABALIN',
  PREGABALIN75: 'PREGABALIN',
  MONTELUKAST10: 'MONTELUKAST',
  PANTOPRAZOLE20: 'PANTOPRAZOLE',
  CALCIUMCARBONATEVITAMIND: 'CALCIUM CARBONATE + VITAMIN D',
  CALCIUMCARBONATEVITD: 'CALCIUM CARBONATE + VITAMIN D',
};

const FORM_PREFIX_PATTERN = /^(TAB|TABLET|CAP|CAPSULE|INJ|INJECTION|NEB|NEBULIZATION|SYP|SYRUP|DROP|DROPS|SUSP|SUSPENSION|CREAM|OINT|OINTMENT)\s+/i;
const FORM_WORD_PATTERN = /\b(TAB|TABLET|CAP|CAPSULE|INJ|INJECTION|NEB|NEBULIZATION|WITH|SYP|SYRUP|DROP|DROPS|SUSP|SUSPENSION)\b/gi;
const STRENGTH_PATTERN = /\b\d+(\.\d+)?\s?(MG|MCG|G|ML|IU|UNIT|UNITS|%)\b/gi;

function compact(value: string): string {
  return value.replace(/[^A-Z0-9]/g, '');
}

export function normalizeBrandName(name: string): string {
  const upper = name
    .toUpperCase()
    .replace(/[^\w\s+.-]/g, ' ')
    .replace(FORM_PREFIX_PATTERN, '')
    .replace(FORM_WORD_PATTERN, ' ')
    .replace(STRENGTH_PATTERN, ' ')
    .replace(/\bSR|XR|MR|CR\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const compacted = compact(upper);
  return OCR_VARIANTS[compacted] || upper;
}

function editDistance(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, () => Array<number>(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i += 1) dp[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) dp[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }

  return dp[a.length][b.length];
}

function getEntryKey(entry: MedicineKnowledgeEntry): string {
  return compact(normalizeBrandName(entry.brandName));
}

function hasAmbiguousText(name: string): boolean {
  return /unclear|illegible|partial|\?|maybe|looks like/i.test(name);
}

function isLowConfidence(confidence?: number): boolean {
  return typeof confidence === 'number' && confidence > 0 && confidence < 0.58;
}

export function resolveMedicineBrand(name: string, confidence?: number): ResolveResult {
  const normalizedName = normalizeBrandName(name);
  const key = compact(normalizedName);

  if (!key || hasAmbiguousText(name) || isLowConfidence(confidence)) {
    return {
      status: 'needs_review',
      confidence: confidence || 0,
      normalizedName,
      possibleMatches: [],
      reason: isLowConfidence(confidence) ? 'Low OCR confidence' : 'Ambiguous medicine name',
    };
  }

  const exact = KNOWLEDGE_BASE.find((entry) => getEntryKey(entry) === key);
  if (exact) {
    return {
      status: 'identified',
      entry: exact,
      confidence: 0.98,
      normalizedName,
      possibleMatches: [],
    };
  }

  const contains = KNOWLEDGE_BASE.filter((entry) => {
    const entryKey = getEntryKey(entry);
    return key.length >= 5 && (key.includes(entryKey) || entryKey.includes(key));
  });

  if (contains.length === 1) {
    return {
      status: 'identified',
      entry: contains[0],
      confidence: 0.92,
      normalizedName,
      possibleMatches: [],
    };
  }

  if (contains.length > 1) {
    return {
      status: 'possible_lookalike',
      confidence: 0.55,
      normalizedName,
      possibleMatches: contains,
      reason: 'Multiple possible brand matches',
    };
  }

  const fuzzyCandidates = KNOWLEDGE_BASE
    .map((entry) => {
      const entryKey = getEntryKey(entry);
      const distance = editDistance(key, entryKey);
      const maxLength = Math.max(key.length, entryKey.length);
      const similarity = 1 - distance / maxLength;

      return { entry, distance, similarity };
    })
    .filter(({ distance, similarity, entry }) => {
      const entryKey = getEntryKey(entry);
      if (key.length < 5 || entryKey.length < 5) return false;
      if (distance <= 1) return true;
      return key.length >= 8 && distance <= 2 && similarity >= 0.82;
    })
    .sort((a, b) => b.similarity - a.similarity || a.distance - b.distance);

  if (fuzzyCandidates.length === 1) {
    return {
      status: 'identified',
      entry: fuzzyCandidates[0].entry,
      confidence: Math.max(0.84, fuzzyCandidates[0].similarity),
      normalizedName,
      possibleMatches: [],
    };
  }

  if (fuzzyCandidates.length > 1) {
    const best = fuzzyCandidates[0];
    const close = fuzzyCandidates.filter((candidate) => best.similarity - candidate.similarity < 0.06);

    if (close.length === 1 && best.similarity >= 0.9) {
      return {
        status: 'identified',
        entry: best.entry,
        confidence: best.similarity,
        normalizedName,
        possibleMatches: [],
      };
    }

    return {
      status: 'possible_lookalike',
      confidence: best.similarity,
      normalizedName,
      possibleMatches: close.map((candidate) => candidate.entry).slice(0, 4),
      reason: 'Possible look-alike medicine name',
    };
  }

  return {
    status: 'needs_review',
    confidence: confidence || 0.6,
    normalizedName,
    possibleMatches: [],
    reason: 'Brand not found in local database',
  };
}

export function resolveMedicine(medicine: Medicine, userVerified = false): Medicine {
  const result = resolveMedicineBrand(medicine.name, medicine.confidence);

  if (result.status === 'identified' && result.entry) {
    const strength = medicine.strength || medicine.dosage || result.entry.strength;

    return {
      ...medicine,
      name: result.entry.brandName,
      resolvedBrandName: result.entry.brandName,
      generic_name: result.entry.genericName,
      genericName: result.entry.genericName,
      strength,
      dosage: medicine.dosage || strength,
      form: result.entry.form,
      category: result.entry.category,
      commonUse: result.entry.commonUse,
      riskNotes: result.entry.riskNotes,
      confidenceSource: result.entry.confidenceSource,
      confidence: Math.max(medicine.confidence || 0, result.confidence),
      resolutionConfidence: result.confidence,
      genericConfirmed: true,
      verificationStatus: 'known',
      identificationStatus: 'identified',
      possibleMatches: [],
    };
  }

  const genericKnown = userVerified && isKnownGenericName(medicine.generic_name || medicine.genericName);

  return {
    ...medicine,
    resolvedBrandName: result.normalizedName || medicine.name,
    genericConfirmed: genericKnown,
    verificationStatus: genericKnown ? 'known' : 'needs_verification',
    identificationStatus: result.status,
    resolutionConfidence: result.confidence,
    possibleMatches: result.possibleMatches.map((entry) => entry.brandName),
  };
}

export function resolveMedicines(medicines: Medicine[], userVerified = false): Medicine[] {
  return medicines.map((medicine) => resolveMedicine(medicine, userVerified));
}


